import { useEffect } from "react";

export interface UseDismissKeyboardOnOutsideTapOptions {
  /**
   * 是否启用。默认启用。
   * 常见用法：在 PC 或某些特殊页面关闭。
   */
  enabled?: boolean;
  /**
   * 作用域选择器：仅当当前 activeElement 位于该作用域内时才会触发 blur。
   * 用于“全局监听 + 局部生效”的场景（例如仅登录弹窗内启用）。
   */
  scopeSelector?: string;
  /**
   * 事件类型。iOS WKWebView 推荐优先 touchstart，pointerdown 作为兜底。
   */
  events?: Array<"touchstart" | "pointerdown" | "mousedown">;
  /**
   * blur 的延迟（毫秒）。默认 0。
   * 在 iOS 上延后到下一轮任务通常更稳，减少“过早 blur 影响点击”的概率。
   */
  blurDelayMs?: number;
}

const DEFAULT_EVENTS: UseDismissKeyboardOnOutsideTapOptions["events"] = [
  "touchstart",
  "pointerdown",
];

const isHTMLElement = (el: unknown): el is HTMLElement =>
  el instanceof HTMLElement;

const isEditableElement = (el: Element | null): el is HTMLElement => {
  if (!isHTMLElement(el)) {
    return false;
  }
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea") {
    return true;
  }
  return Boolean(el.isContentEditable);
};

const hasEditableAncestor = (el: Element | null): boolean => {
  let cur: Element | null = el;
  while (cur) {
    if (isEditableElement(cur)) {
      return true;
    }
    cur = cur.parentElement;
  }
  return false;
};

/**
 * 点击/触摸输入控件之外时，让当前聚焦元素 blur()，从而收起键盘。
 * 适用于 iOS WKWebView / H5：使用 capture 阶段尽早捕获事件，避免被 stopPropagation 影响。
 */
export const useDismissKeyboardOnOutsideTap = (
  options: UseDismissKeyboardOnOutsideTapOptions = {},
) => {
  const {
    enabled = true,
    events = DEFAULT_EVENTS,
    scopeSelector,
    blurDelayMs = 0,
  } = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (typeof document === "undefined") {
      return;
    }

    const handler = (event: Event) => {
      const active = document.activeElement;
      if (!isEditableElement(active)) {
        return;
      }

      if (scopeSelector && !active.closest(scopeSelector)) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;

      // 如果点到输入控件内部（含 contenteditable），不应 blur
      // - 目标在当前 active 内部
      // - 或者目标本身/祖先就是另一个可编辑控件
      if (target) {
        if (active.contains(target)) {
          return;
        }
        if (hasEditableAncestor(target)) {
          return;
        }
      }

      // iOS 上过早 blur 可能影响点击链路，延后到下一轮任务更稳
      window.setTimeout(
        () => {
          // active 可能在这段时间被切换，保持“只 blur 当下仍是原元素”更安全
          if (document.activeElement === active) {
            active.blur();
          }
        },
        Math.max(0, blurDelayMs),
      );
    };

    const add = (type: string) => {
      document.addEventListener(type, handler, {
        capture: true,
        passive: true,
      });
    };
    const remove = (type: string) => {
      document.removeEventListener(type, handler, {
        capture: true,
      } as AddEventListenerOptions);
    };

    events.forEach(add);
    return () => events.forEach(remove);
  }, [blurDelayMs, enabled, events, scopeSelector]);
};
